import { api } from "@helpers/api";
import { IUser } from "@/types/IUser";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import { useEffect, useState, useRef, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { AxiosError } from "axios";
import RenderPage from "@components/common/RenderPage";
import { useSearchParams } from "react-router-dom";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

interface CallData {
  user: IUser;
  answered: number;
  not_answered: number;
}

const useFetchCallData = (sdrTeams: string | null) => {
  const [data, setData] = useState<CallData[] | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AxiosError | undefined>(undefined);

  useEffect(() => {
    api
      .get<CallData>(`/crm/sdr/callsToday?tags=${sdrTeams}`)
      .then((e) => setData(e.data))
      .catch((e: AxiosError) => setError(e))
      .finally(() => setLoading(false));
  }, [sdrTeams]);

  return { loading, data, setData, error };
};

const getDynamicXOffset = (barHeight: number) => {
  const minHeight = 30;
  const maxHeight = 300;
  const minOffset = 20
  const maxOffset = 240;

  const clampedHeight = Math.max(minHeight, Math.min(barHeight, maxHeight));

  const normalized = (clampedHeight - minHeight) / (maxHeight - minHeight);
  return minOffset + normalized * (maxOffset - minOffset);
};

const StackedBarChart = () => {
  const [searchParams] = useSearchParams();
  const sdrTeams = searchParams.get("tags");

  const { loading, data, setData, error } = useFetchCallData(sdrTeams);
  const chartRef = useRef<ChartJS | null>(null);
  const filteredDataRef = useRef({ labels: [], datasets: [], customEmails: [] });

  const getCalls = async () => {
    await api.get(`/crm/sdr/callsToday?tags=${sdrTeams}`).then((e) => {
      setData(e.data);
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      getCalls();
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [sdrTeams]);

  data?.sort(
    (a, b) => b.answered + b.not_answered - (a.answered + a.not_answered)
  );

  const filteredData = data
    ? {
      labels: data.map((item) => item.user.name),
      datasets: [
        {
          label: "Answered Calls",
          data: data.map((item) => item.answered),
          backgroundColor: "rgba(27, 98, 242, 1)",
          stack: "stack0",
          borderWidth: 1,
        },
        {
          label: "Unanswered Calls",
          data: data.map((item) => item.not_answered),
          backgroundColor: "rgba(232, 100, 82, 1)",
          stack: "stack0",
          borderWidth: 1,
        },
      ],
      customEmails: data.map((item) =>
        item.user.email.split("@opendatacenter.com.br")[0]
      ),
    }
    : { labels: [], datasets: [], customEmails: [] };


  useEffect(() => {
    filteredDataRef.current = filteredData;
  }, [filteredData]);

  const plugins = useMemo(() => {
    const userImage = {
      id: "userImage",
      afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const currentData = filteredDataRef.current;

        if (filteredDataRef.current.customEmails.length != 1) {
          currentData.customEmails.forEach((image, index) => {
            const meta0 = chart.getDatasetMeta(0);
            const bar = meta0.data[index];
            const barHeight = bar.height;

            const imageSize = Math.min(barHeight - 4);
            const radius = imageSize / 2;

            const xPos = bar.x - radius
            const yPos = bar.y


            const chartImage = new Image();
            chartImage.src = `https://painel.opendatacenter.com.br/assets/img/avatars/${image}.png`;

            chartImage.onload = () => {
              ctx.save();
              ctx.beginPath();
              ctx.arc(xPos + radius, yPos, radius, 0, Math.PI * 2);
              ctx.clip();
              ctx.drawImage(chartImage, xPos, yPos - radius, imageSize, imageSize);
              ctx.restore();
            }
          });
        };
      },
    };

    const blueLabel = {
      id: "blueLabel",
      afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const currentData = filteredDataRef.current;

        currentData.customEmails.forEach((_, index) => {

          const value = currentData.datasets[0].data[index];
          if (value === 0) return;

          const meta0 = chart.getDatasetMeta(0);
          const bar = meta0.data[index];
          const barHeight = bar.height;
          const fontSize = Math.max(10, barHeight * 0.5);

          const xPosition = bar.x - getDynamicXOffset(barHeight);
          const yPosition = bar.y

          ctx.font = `bold ${fontSize}px sans-serif`;
          ctx.fillStyle = "white";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(value, xPosition, yPosition);
        });
      },
    };

    const redLabel = {
      id: "redLabel",
      afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const currentData = filteredDataRef.current;

        currentData.customEmails.forEach((_, index) => {
          const value = currentData.datasets[1].data[index];
          if (value === 0) return;

          const meta0 = chart.getDatasetMeta(1);
          const bar = meta0.data[index];
          const barHeight = bar.height;
          const fontSize = Math.max(10, barHeight * 0.5);

          const xPosition = bar.x - bar.width + getDynamicXOffset(barHeight)
          const yPosition = bar.y

          ctx.font = `bold ${fontSize}px sans-serif`;
          ctx.fillStyle = "white";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(value, xPosition, yPosition);
        });
      },
    };

    const total = {
      id: "total",
      afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const currentData = filteredDataRef.current;

        currentData.customEmails.forEach((_, index) => {
          const meta1 = chart.getDatasetMeta(1);
          const bar = meta1.data[index];
          const barHeight = bar.height;
          const fontSize = Math.max(10, barHeight * 0.5);
          const totalValue = currentData.datasets[1].data[index] + currentData.datasets[0].data[index];

          const xPosition = bar.x + getDynamicXOffset(barHeight)
          const yPosition = bar.y

          ctx.font = `bold ${fontSize}px sans-serif`;
          ctx.fillStyle = "white";
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(totalValue, xPosition, yPosition);
        });
      },
    };

    const metaLine = {
      id: "metaLine",
      afterDatasetsDraw(chart) {
        const {
          ctx,
          scales: { x },
          chartArea: { left, right },
        } = chart;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x.getPixelForValue(40), left - 220);
        ctx.lineTo(x.getPixelForValue(40), right + 100);
        ctx.strokeStyle = "rgba(22, 255, 8, 1)";
        ctx.lineWidth = 3;
        ctx.stroke();
      },
    };

    return [userImage, metaLine, blueLabel, redLabel, total];
  }, []);

  const options = {
    type: "bar",
    indexAxis: "y",
    scales: {
      x: {
        stacked: true,
        grace: 0,
        min: 0,
        max: () => {
          const maxTotal = Math.max(
            ...data.map(item => item.answered + item.not_answered)
          );

          return Math.max(maxTotal + 5);
        },
      },
      y: {
        beginAtZero: true,
        stacked: true,
        min: 0,
        ticks: {
          color: "white",
          padding: filteredDataRef.current.customEmails.length <= 3 ? 150 : 20,
          font: {
            size: 16,
            weight: 'bold'
          }
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  return (
    <RenderPage data={data} loading={loading} error={error}>
      <div style={{ height: "100vh", maxHeight: "100vh", background: "black" }}>
        <Bar
          data={filteredData}
          options={options}
          plugins={plugins}
          ref={chartRef}
        />
      </div>
    </RenderPage>
  );
};
export default StackedBarChart;
